// Extracted from the portal's Elinx page helpers (buildElinxRows.helper):
// the only two pieces the pay/collect dialogs consume. Pure functions —
// locked options and account facts arrive as arguments.
import { BankingPaymentRail, PaymentMethod, PaymentMethodType } from 'vopay';
import { startOfDay, isEqual } from 'date-fns';

export type ElinxFormType = 'Pay' | 'Collect' | 'Connect' | 'Flex';

export type ElinxCountry = 'CA' | 'US';

// The account's Country arrives as a plain string from the host; unknown
// values behave like "no country" (no country-gated option is offered).
export function toElinxCountry(country: string): ElinxCountry | undefined {
  return country === 'CA' || country === 'US' ? country : undefined;
}

type PaymentSelectionValue =
  | 'bank'
  | 'credit'
  | 'debitcard'
  | 'email'
  | 'googlepay'
  | 'applepay'
  | 'paypal'
  | 'venmo'
  | 'zelle'
  | '';

function isToday(dateStr: string): boolean {
  const date = startOfDay(new Date(Number(dateStr)));
  const today = startOfDay(new Date());

  return isEqual(date, today);
}

const mapToPaymentSelectionValue = (paymentMethod: PaymentMethodType): PaymentSelectionValue => {
  const mapping: Record<PaymentMethodType, Exclude<PaymentSelectionValue, ''>> = {
    BankAccount: 'bank',
    CreditCard: 'credit',
    DebitCard: 'debitcard',
    PhoneNumber: 'email',
    Email: 'email',
    GooglePay: 'googlepay',
    ApplePay: 'applepay',
    PayPal: 'paypal',
    Venmo: 'venmo',
    Zelle: 'zelle',
  };

  return mapping[paymentMethod] || '';
};

export const getDefaultBankingPaymentRails = (
  formType: ElinxFormType,
  country: ElinxCountry | undefined,
  lockedSelectOptions: Record<string, string> | undefined,
  scheduleStartDate?: string,
  frequency?: string,
): string => {
  type BankingRailOption = {
    value: BankingPaymentRail;
    featureKey: string;
    visible: boolean;
    isRecurringEnabled: boolean;
  };

  const options: BankingRailOption[] = [
    {
      value: 'eft',
      featureKey: 'eft',
      visible: country === 'CA',
      isRecurringEnabled: true,
    },
    {
      value: 'vopayinstant',
      featureKey: 'VoPay Instant',
      visible: country === 'CA' && formType === 'Pay',
      isRecurringEnabled: false,
    },
    {
      value: 'ach',
      featureKey: 'USD',
      visible: country === 'US',
      isRecurringEnabled: true,
    },
    {
      value: 'rtp',
      featureKey: 'RTP',
      visible: country === 'US' && formType === 'Pay',
      isRecurringEnabled: false,
    },
    {
      value: 'fedwire',
      featureKey: 'Wire',
      visible: country === 'US' && formType === 'Pay',
      isRecurringEnabled: false,
    },
  ];

  const isTodaySchedule = scheduleStartDate ? isToday(scheduleStartDate) : true;
  const isSingleFrequency = frequency === 'single' || !frequency;

  const availableOptions = options.filter((option) => {
    if (!option.visible) return false;
    // Check if locked via LockedSelectOptions
    if (lockedSelectOptions && option.featureKey in lockedSelectOptions) return false;
    // If not today or not single frequency, only allow recurring-enabled rails
    if (!(isTodaySchedule && isSingleFrequency) && !option.isRecurringEnabled) return false;

    return true;
  });

  return availableOptions.map((opt) => opt.value).join(',');
};

export const getDefaultPaymentSelectionMethods = (
  formType: ElinxFormType,
  country: ElinxCountry | undefined,
  lockedSelectOptions: Record<string, string> | undefined,
  scheduleStartDate?: string,
  frequency?: string,
  paymentMethods?: PaymentMethod[],
  paymentMethodDisplayOptions?: string,
): string => {
  type PaymentOption = {
    value: string;
    featureKey: string;
    visible: boolean;
  };

  const options: PaymentOption[] = [
    {
      value: 'bank',
      featureKey: 'Bank Account',
      visible: true,
    },
    {
      value: 'email',
      featureKey: formType === 'Pay' ? 'Interac e-Transfer' : 'Interac e-Transfer Money Request',
      visible: country === 'CA' && formType !== 'Flex' && formType !== 'Connect',
    },
    {
      value: 'email',
      featureKey: 'Email',
      visible: country === 'CA' && formType === 'Connect',
    },
    {
      value: 'credit',
      featureKey: 'Credit Card',
      visible: formType !== 'Pay',
    },
    {
      value: 'debitcard',
      featureKey: 'Debit Card',
      visible: formType === 'Pay' || formType === 'Connect',
    },
    {
      value: 'googlePay',
      featureKey: 'Google Pay',
      visible: formType === 'Collect' || formType === 'Flex',
    },
    {
      value: 'paypal',
      featureKey: 'PayPal',
      visible: formType === 'Pay' || formType === 'Connect',
    },
    {
      value: 'venmo',
      featureKey: 'Venmo',
      visible: (formType === 'Pay' || formType === 'Connect') && country === 'US',
    },
    {
      value: 'zelle',
      featureKey: 'Zelle',
      visible: (formType === 'Pay' || formType === 'Connect') && country === 'US',
    },
  ];

  const isTodaySchedule = scheduleStartDate ? isToday(scheduleStartDate) : true;
  const isSingleFrequency = frequency === 'single' || !frequency;

  // Filter based on visibility, locked status, and scheduling constraints
  let availableOptions = options.filter((option) => {
    if (!option.visible) return false;
    // Check if locked via LockedSelectOptions
    if (lockedSelectOptions && option.featureKey in lockedSelectOptions) return false;
    // If not today or not single frequency, only allow bank
    if (!(isTodaySchedule && isSingleFrequency) && option.value !== 'bank') return false;

    return true;
  });

  // If PaymentMethodDisplayOptions is "Existing", filter by available payment methods
  if (paymentMethodDisplayOptions === 'Existing' && paymentMethods) {
    const existingPaymentMethodValues = new Set<string>(
      paymentMethods.map((pm) => mapToPaymentSelectionValue(pm.PaymentMethod)),
    );

    availableOptions = availableOptions.filter((option) => existingPaymentMethodValues.has(option.value));
  }

  // Remove duplicates and join
  const uniqueValues = [...new Set(availableOptions.map((opt) => opt.value))];

  return uniqueValues.join(',');
};
